import { AlertTriangle, Check, Coins, CreditCard, Receipt, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { PLANS, CREDIT_PACKS } from '@/lib/lemonsqueezy';
import { useBillingStore } from '@/stores/billingStore';

type ResolvedPlan = {
  id: string;
  name: string;
  description: string;
  price: number;
  priceId: string;
  credits: number;
  trialCredits: number;
  features: string[];
  popular?: boolean;
  billing?: string;
  trial?: number;
  originalPrice?: number;
  providerKey?: string;
};

type ResolvedCreditPack = {
  id: string;
  credits: number;
  price: number;
  priceId: string;
};

function formatPlanPrice(plan: ResolvedPlan) {
  if (plan.price === 0) {
    return '$0';
  }

  return `$${plan.price.toLocaleString()}`;
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString();
}

export function PlansPage() {
  const {
    currentPlan,
    subscription,
    isLoading,
    fetchBillingSummary,
    fetchCatalog,
    plansCatalog,
    creditPacksCatalog,
    transactions,
    fetchTransactions,
    invoices,
    fetchInvoices,
    createCheckout,
  } = useBillingStore();

  useEffect(() => {
    void fetchBillingSummary();
    void fetchCatalog();
    void fetchTransactions();
    void fetchInvoices();
  }, [fetchBillingSummary, fetchCatalog, fetchTransactions, fetchInvoices]);

  const resolvedPlans: ResolvedPlan[] =
    plansCatalog.length > 0
      ? plansCatalog.map((plan) => ({
          id: plan.slug,
          name: plan.name,
          description: plan.description ?? `${plan.creditsMonthly.toLocaleString()} credits/month`,
          price: Math.round((plan.priceMonthlyCents ?? 0) / 100),
          priceId: plan.lsVariantId ?? plan.slug,
          credits: plan.creditsMonthly,
          trialCredits: plan.trialCredits,
          features: [
            `${plan.creditsMonthly.toLocaleString()} credits/month`,
            plan.trialCredits > 0 ? `${plan.trialCredits.toLocaleString()} trial credits` : null,
            `${plan.personasLimit === -1 ? 'Unlimited' : plan.personasLimit} managed personas`,
            `${plan.targetsLimit === -1 ? 'Unlimited' : plan.targetsLimit} network people`,
          ].filter((feature): feature is string => Boolean(feature)),
          popular: plan.slug === 'pro',
          billing: plan.billingCycle === 'annual' ? 'year' : plan.billingCycle === 'weekly' ? 'week' : 'month',
          trial: plan.trialCredits > 0 ? 3 : undefined,
          originalPrice: undefined,
          providerKey: plan.providerKey,
        }))
      : PLANS.map((plan) => ({ ...plan, trialCredits: 0, providerKey: 'migration_fallback' }));

  const resolvedCreditPacks: ResolvedCreditPack[] =
    creditPacksCatalog.length > 0
      ? creditPacksCatalog.map((pack) => ({
          id: pack.slug,
          credits: pack.creditsAmount,
          price: Number(pack.price),
          priceId: pack.lsVariantId ?? pack.slug,
        }))
      : [...CREDIT_PACKS];

  const activePlan = resolvedPlans.find((plan) => plan.id === currentPlan.id);
  const planName = activePlan?.name ?? currentPlan.name;
  const planCredits = activePlan?.credits ?? currentPlan.credits;
  const isFallbackCatalog = plansCatalog.length === 0;
  const hasFallbackProviders = resolvedPlans.some((plan) => plan.providerKey === 'migration_fallback');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="brutalist-panel relative overflow-hidden rounded-3xl p-6 lg:p-8">
        <div className="absolute right-6 top-6 hidden rounded-full border-2 border-border px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground lg:block">
          {planName} plan
        </div>
        <div className="max-w-4xl">
          <p className="brutalist-label">BeLoud Billing</p>
          <h1 className="mt-3 text-4xl font-black leading-none tracking-[-0.08em] md:text-6xl">
            Pricing for managed LinkedIn publishing.
          </h1>
          <p className="mt-4 max-w-2xl text-base font-medium text-muted-foreground md:text-lg">
            Choose a BeLoud plan, add one-time credits when production spikes, and keep billing activity visible from one place.
          </p>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border-2 border-border bg-card p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Current plan</p>
            <p className="mt-2 text-3xl font-black tracking-[-0.08em]">{planName}</p>
          </div>
          <div className="rounded-2xl border-2 border-border bg-card p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Included credits</p>
            <p className="mt-2 text-3xl font-black tracking-[-0.08em]">{planCredits.toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border-2 border-border bg-card p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Billing state</p>
            <p className="mt-2 text-3xl font-black capitalize tracking-[-0.08em]">
              {subscription?.status ?? 'free'}
            </p>
          </div>
        </div>
      </section>

      {isFallbackCatalog || hasFallbackProviders ? (
        <section className="brutalist-panel-soft flex flex-col gap-3 rounded-2xl border-amber-500/60 bg-amber-500/10 p-4 text-amber-900 dark:text-amber-100 md:flex-row md:items-start">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-black uppercase tracking-[0.16em]">Migration catalog fallback</p>
            <p className="mt-1 text-sm font-bold">
              Showing local fallback pricing until the backend billing catalog returns live BeLoud products and Lemon Squeezy variants.
            </p>
          </div>
        </section>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-3">
        {resolvedPlans.map((plan) => {
          const isCurrentPlan = currentPlan.id === plan.id;

          return (
            <article
              key={plan.id}
              className={`brutalist-panel-soft relative flex min-h-full flex-col rounded-3xl p-5 transition-transform hover:-translate-y-1 ${plan.popular ? 'border-primary bg-primary/5' : ''}`}
            >
              {plan.popular ? (
                <div className="absolute -top-3 left-5">
                  <span className="status-acid rounded-full border border-border px-3 py-1 text-[11px] font-black uppercase tracking-wide">
                    Popular
                  </span>
                </div>
              ) : null}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="brutalist-label">Plan</p>
                  <h2 className="mt-2 text-3xl font-black tracking-[-0.08em]">{plan.name}</h2>
                  <p className="mt-2 text-sm font-medium text-muted-foreground">{plan.description}</p>
                </div>
                {isCurrentPlan ? (
                  <span className="rounded-full border-2 border-border bg-card px-3 py-1 text-xs font-black uppercase tracking-wide">
                    Current
                  </span>
                ) : null}
              </div>

              <div className="mt-5 rounded-2xl border-2 border-border bg-background/45 p-4">
                <div className="flex flex-wrap items-end gap-2">
                  <span className="text-5xl font-black tracking-[-0.08em]">{formatPlanPrice(plan)}</span>
                  {plan.price > 0 ? <span className="pb-2 text-sm font-black text-muted-foreground">/{plan.billing || 'month'}</span> : null}
                  {plan.originalPrice ? (
                    <span className="pb-2 text-sm font-bold text-muted-foreground line-through">${plan.originalPrice}</span>
                  ) : null}
                </div>
                <p className="mt-3 text-sm font-bold text-muted-foreground">
                  {plan.trial ? `${plan.trial}-day free trial` : plan.trialCredits > 0 ? `${plan.trialCredits.toLocaleString()} trial credits included` : 'Straightforward usage-based production credits'}
                </p>
              </div>

              <ul className="mt-5 space-y-2">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm font-bold">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                className={`mt-auto w-full rounded-xl border-2 border-border py-3 text-sm font-black transition-transform hover:-translate-y-0.5 ${
                  isCurrentPlan
                    ? 'bg-card text-foreground'
                    : 'bg-primary text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.22)] dark:shadow-[4px_4px_0_hsl(var(--primary)/0.24)]'
                }`}
                variant={isCurrentPlan ? 'outline' : 'default'}
                disabled={isLoading || isCurrentPlan}
                onClick={() => {
                  if (plan.id === 'free') {
                    // Can't upgrade to free
                  } else {
                    if (plan.priceId) {
                      void createCheckout('plan', plan.id);
                    }
                  }
                }}
              >
                {isCurrentPlan ? 'Current Plan' : 'Upgrade'}
              </Button>
            </article>
          );
        })}
      </section>

      <section className="brutalist-panel-soft rounded-3xl p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="brutalist-label">One-time credits</p>
            <h2 className="mt-2 text-3xl font-black tracking-[-0.08em]">Credit packs for publishing bursts.</h2>
            <p className="mt-2 max-w-2xl text-sm font-medium text-muted-foreground">
              Add credits without changing subscription level. Packs are purchased through the same backend checkout flow as plans.
            </p>
          </div>
          <div className="rounded-xl border-2 border-border bg-card px-4 py-2 text-sm font-black text-muted-foreground">
            {resolvedCreditPacks.length} packs available
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {resolvedCreditPacks.map((pack) => (
            <article key={pack.id} className="rounded-3xl border-2 border-border bg-background/45 p-5 transition-transform hover:-translate-y-1">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-secondary">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Credit pack</p>
                  <h3 className="mt-1 text-2xl font-black tracking-[-0.06em]">{pack.credits.toLocaleString()}</h3>
                </div>
              </div>
              <div className="mt-5 flex items-end justify-between gap-3">
                <p className="text-4xl font-black tracking-[-0.08em]">${pack.price.toLocaleString()}</p>
                <Button
                  className="rounded-xl border-2 border-border font-black"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => {
                    if (pack.priceId) {
                      void createCheckout('credit_pack', pack.id);
                    }
                  }}
                >
                  Buy Now
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="brutalist-panel-soft rounded-3xl p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-secondary">
              <Coins className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="brutalist-label">Credit ledger</p>
              <h2 className="mt-1 text-2xl font-black tracking-[-0.06em]">Recent credit activity</h2>
              <p className="mt-1 text-sm font-medium text-muted-foreground">Latest token usage, grants and account credit movements.</p>
            </div>
          </div>

          <div className="mt-5">
            {transactions.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-border bg-background/45 p-6 text-center">
                <CreditCard className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-lg font-black tracking-[-0.06em]">No transactions yet</p>
                <p className="mx-auto mt-1 max-w-sm text-sm font-medium text-muted-foreground">
                  Credit grants, purchases and generation usage will appear here after billing activity starts.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 8).map((tx) => (
                  <div key={tx.id} className="rounded-2xl border-2 border-border bg-background/45 p-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-black">{tx.actionType ?? tx.description}</span>
                      <span className={tx.amount < 0 ? 'font-black text-green-500' : 'font-black text-foreground'}>
                        {tx.amount < 0 ? '+' : '-'}{Math.abs(tx.amount)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      {tx.modelSlug ? `${tx.modelSlug}` : 'manual/system'}
                      {tx.inputTokens !== undefined || tx.outputTokens !== undefined
                        ? ` | ${tx.inputTokens ?? 0} in / ${tx.outputTokens ?? 0} out`
                        : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="brutalist-panel-soft rounded-3xl p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-secondary">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="brutalist-label">Invoices</p>
              <h2 className="mt-1 text-2xl font-black tracking-[-0.06em]">Subscription invoices</h2>
              <p className="mt-1 text-sm font-medium text-muted-foreground">Completed and open invoice records from the billing provider.</p>
            </div>
          </div>

          <div className="mt-5">
            {invoices.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-border bg-background/45 p-6 text-center">
                <Receipt className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-lg font-black tracking-[-0.06em]">No invoices available</p>
                <p className="mx-auto mt-1 max-w-sm text-sm font-medium text-muted-foreground">
                  Invoices will appear here once paid or open subscription billing records exist.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.slice(0, 8).map((invoice) => (
                  <div key={invoice.id} className="rounded-2xl border-2 border-border bg-background/45 p-3">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-black capitalize">{invoice.status}</span>
                      <span className="font-black">
                        {(invoice.amount / 100).toLocaleString(undefined, {
                          style: 'currency',
                          currency: invoice.currency || 'EUR',
                        })}
                      </span>
                    </div>
                    <div className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                      {formatDate(invoice.invoiceDate)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
